import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { decodeJwt, showError, showSuccess } from "../../utils";
import { getSinglePlan, updateSinglePlan } from "../../services/supportplan";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Loader,
  Select,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import useStyles from "./style";
import moment from "moment";
import ServicePlanAccordion from "../../components/ServicePlanComponents/ServicePlanAccordion";
import  {
  assistiveDevicesItems,
  bedCheck,
  brushingTeeth,
  dressing,
  feedingAssistanceItems,
  grooming,
  hearingItems,
  hydrationItems,
  mealLocationItems,
  nutritionAndHydration,
  nutritionItems,
  other,
  shower,
  skinCare,
  speechItems,
  toileting,
  undergarments,
  visionItems,
} from "../../components/ServicePlanComponents/ServicePlanSteps/AssessmentStep";
import { useSelector } from "react-redux";
import AccordionCheck from "../../components/AccordionCheck";
import  {
  hiddenFieldsCareplanAdlConfig,
} from "../../components/ServicePlanComponents/ServicePlanSteps/ActivitiesOfDailyLivingStep";
import  {
  cognitiveActivitiesItems,
  cognitiveStatusItems,
  conditionsRequiringCaregiversItems,
  coordinationOfCareItems,
  familyRepCommunicationItems,
  medicationAdministrationItems,
  mobilityAmbulationTransferItems,
  nursingMedicalInstructionsItems,
  safetyMonitoringItems,
} from "../../components/ServicePlanComponents/ServicePlanSteps/CareAndSafetyStep";
import { FiCalendar, FiEdit, } from "react-icons/fi";
import { DateInput } from "@mantine/dates";
import SignaturePad from "signature_pad";
import { uploadFileToS3Api } from "../../utils/PresignedUrlFunction";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { TbKeyboard } from "react-icons/tb";
import { CiImageOn } from "react-icons/ci";
import { IoIosCloseCircle } from "react-icons/io";
import { getServicePlanBlob } from "../../components/ServicePlanPreview";
import WhiteCheckMarkSvg from "../../components/Svgs/WhiteCheckMarkSvg";
import { FaUndo } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";

export const ServicePlanPage = () => {
  const { classes, cx } = useStyles();
  const { token, mode } = useParams();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskTypeDetails, setTypeDetails] = useState(null);
  const [checks, setChecks] = useState([]);
  const { profile } = useSelector((state) => {
    return state.profile;
  });
  const [signatures, setSignatures] = useState([]);
  const [active, setActive] = useState(0);
  const [signatureItem, setSignatureItem] = useState(null);
  const [isAddSignature, setIsAddSignature] = useState(false);
  const [currentTab, setCurrentTab] = useState("draw");
  const [signatureTextValue, setSignatureTextValue] = useState("");
  const signatureCanvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const initialErrorState = {
    signature: "",
    fullName: "",
    title: "",
    date: "",
  };
  const [error, setError] = useState(initialErrorState);
  // Use useMemo to prevent recreating decoded object on every render
  const { decoded, isExpired } = useMemo(() => {
    try {
      const decodedToken = decodeJwt(token);
      const expired = Date.now() > decodedToken.expireLinkAt;
      return { decoded: decodedToken, isExpired: expired };
    } catch (error) {
      return { decoded: null, isExpired: true };
    }
  }, [token]); // Only recalculate when token changes
  console.log("🚀 ~ ServicePlanPage ~ decoded:", decoded);

  // Use useCallback to memoize the fetch function
  const getPlan = useCallback(async () => {
    if (!decoded?.carePlanId) return;

    try {
      setLoading(true);
      const res = await getSinglePlan(decoded.carePlanId);
      console.log("🚀 ~ ServicePlanPage ~ res:", res);
      if (res.success) {
        setPlanData(res.data.plan);
        setTypeDetails(res?.data?.taskType);
      }
    } catch (error) {
      console.error("Error fetching plan:", error);
      showError("Failed to load service plan", "error", false);
    } finally {
      setLoading(false);
    }
  }, [decoded?.carePlanId]); // Only recreate when carePlanId changes

  useEffect(() => {
    getPlan();
  }, [getPlan]); // Now depends on memoized function

  useEffect(() => {
    if (
      planData?.servicePlanData?.activitiesOfDailyLiving?.fieldsDone &&
      planData?.servicePlanData?.activitiesOfDailyLiving.fieldsDone.length >
        0 &&
      checks.length === 0
    ) {
      setChecks(planData?.servicePlanData?.activitiesOfDailyLiving.fieldsDone);
    }
  }, [planData?.servicePlanData?.activitiesOfDailyLiving?.fieldsDone]);

  useEffect(() => {
    if (planData?.servicePlanData?.signature) {
      setSignatures(planData?.servicePlanData?.signature);
    }
  }, [planData?.servicePlanData?.signature]);

  useEffect(() => {
    const checkCanvas = () => {
      const canvas = signatureCanvasRef.current;

      if (canvas) {
        signaturePadRef.current?.off(); // remove old listeners
        const parent = canvas.parentElement;
        canvas.width = parent?.offsetWidth || 620;
        canvas.height = 160;
        signaturePadRef.current = new SignaturePad(canvas, {
          backgroundColor: "rgba(255,255,255,0)",
          penColor: "rgb(0,0,0)",
        });
      } else {
        setTimeout(checkCanvas, 50);
      }
    };

    checkCanvas();
  }, [signatureItem?.id]);

  // Check for expired/invalid token
  if (!decoded || isExpired) {
    return showError(
      "This link has expired. Please request a new one.",
      "expired",
      false,
    );
  }

  if (loading) {
    return (
      <Center h="100vh">
        <Loader color="yellow" />
      </Center>
    );
  }

  if (!planData) {
    return (
      <Center h="100vh">
        <p className="error">No plan data available</p>
      </Center>
    );
  }

  const { client, servicePlanData, startDate, endDate, planType, status } =
    planData;
  const { clientData, assessment, careAndSafety, signature } = servicePlanData;

  const handleSubmit = async () => {
    if (!signatureItem) return;
    let errors = { ...initialErrorState };
    if (!signatureItem.fullName?.trim()) {
      errors.fullName = "Full name is required";
    }
    if (!signatureItem.title?.trim()) {
      errors.title = "Title is required";
    }
    if (!signatureItem.date) {
      errors.date = "Date is required";
    }

    setLoading(true);
    try {
      let uploadedSignatureURL = null;
      let uploadedSignatureImage = null;
      let uploadedSignatureText = null;

      if (currentTab === "draw") {
        const hasDrawing =
          signaturePadRef.current && !signaturePadRef.current.isEmpty();
        if (!hasDrawing) {
          errors.signature = "Signature is required";
        } else {
          const dataUrl = signaturePadRef.current.toDataURL("image/png");
          const res = await fetch(dataUrl);
          const blob = await res.blob();
          const fileToUpload = new File(
            [blob],
            `signature_${Date.now()}.png`,
            { type: "image/png" },
          );
          const result = await uploadFileToS3Api({ file: fileToUpload });
          uploadedSignatureURL = result?.uploadURL;
        }
      } else if (currentTab === "image") {
        if (!selectedImage) {
          errors.signature = "Signature is required";
        } else {
          const result = await uploadFileToS3Api({ file: selectedImage });
          uploadedSignatureImage = result?.uploadURL;
        }
      } else if (currentTab === "type") {
        if (!signatureTextValue) {
          errors.signature = "Signature is required";
        } else {
          uploadedSignatureText = signatureTextValue;
        }
      }

      if (Object.values(errors).some((error) => error)) {
        setError(errors);
        setLoading(false);
        return;
      } else {
        setError(initialErrorState);
      }

      const updatedSignaturesList = [...signatures];
      const existingIndex = updatedSignaturesList.findIndex(
        (sig) => sig.email === decoded?.email,
      );
      const {
        signature: _s,
        signatureImage: _si,
        signatureText: _st,
        ...restSignatureItem
      } = signatureItem;
      const updatedSignatureItem = {
        ...restSignatureItem,
        signatureRequestStatus: "success",
        timestamp: new Date().toISOString(),
        ...(uploadedSignatureURL && { signature: uploadedSignatureURL }),
        ...(uploadedSignatureImage && { signatureImage: uploadedSignatureImage }),
        ...(uploadedSignatureText && { signatureText: uploadedSignatureText }),
      };

      if (existingIndex !== -1) {
        updatedSignaturesList[existingIndex] = updatedSignatureItem;
      } else {
        updatedSignaturesList.push(updatedSignatureItem);
      }
      const updatedPlan = {
        id: decoded?.carePlanId,
        ...planData,
        servicePlanData: {
          ...servicePlanData,
          signature: updatedSignaturesList,
        },
      };

      const res = await updateSinglePlan(
        updatedPlan,
        signatureItem.fullName,
        token,
      );
      if (res.success) {
        getPlan();
        setIsAddSignature(false);
        setSignatureItem(null);
        handleClearSignature();
        showSuccess("Signature added successfully");
      }
    } catch (error) {
      showError("Failed to update service plan", "error", false);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSignature = () => {
    signaturePadRef.current?.clear();
    setSignatureTextValue("");
    setSelectedImage(null);
  };

  const handleTabChange = (value) => {
    if (currentTab === "draw") {
      setSignatureTextValue("");
      setSelectedImage(null);
      setCurrentTab(value);
    } else if (currentTab === "type") {
      signaturePadRef.current?.clear();
      setSelectedImage(null);
      setCurrentTab(value);
    } else {
      signaturePadRef.current?.clear();
      setSignatureTextValue("");
      setCurrentTab(value);
    }
  };

  const handleSignFieldChange = (field, value) => {
    setSignatureItem({
      ...signatureItem,
      [field]: value,
    });
  };

  const handleDownloadPDF = async () => {
    try {
      const blob = await getServicePlanBlob({
        planPayload: planData,
        taskTypeDetails,
        businessName: decoded?.businessName || "Service Plan",
        email: decoded?.email
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `service_plan_${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
      showError("Failed to download PDF", "error", false);
    }
  };

  const userSign = signatures?.find((sig) => sig?.email === decoded?.email);

  const steps = [
    {
      label: "Client Data",
      component: (
        <div className={classes.clientDataContainer}>
          <Grid gutter={17}>
            <Grid.Col span={12} sm={6} md={4} lg={4}>
              <TextInput
                label="Facility"
                readOnly
                className={classes.inputStyle}
                value={planData?.facility?.name || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={4} lg={4}>
              <TextInput
                label="Date"
                readOnly
                className={classes.inputStyle}
                value={
                  startDate && endDate
                    ? `${moment(startDate).format("MM/DD/YYYY")} - ${moment(endDate).format("MM/DD/YYYY")}`
                    : "-"
                }
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={4} lg={4}>
              <TextInput
                label="Assessment Type"
                readOnly
                className={classes.inputStyle}
                value={clientData?.assessmentType || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <h6 className={classes.sectionHdng}>Intake Data</h6>
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={4} lg={4}>
              <TextInput
                label="First Name"
                readOnly
                className={classes.inputStyle}
                value={clientData?.firstName || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={4} lg={4}>
              <TextInput
                label="Last Name"
                readOnly
                className={classes.inputStyle}
                value={clientData?.lastName || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={4} lg={4}>
              <TextInput
                label="Admission Date"
                readOnly
                className={classes.inputStyle}
                value={
                  moment(clientData?.admissionDate).format("MM/DD/YYYY") || "-"
                }
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Date of Birth"
                readOnly
                className={classes.inputStyle}
                value={
                  moment(clientData?.dateOfBirth).format("MM/DD/YYYY") || "-"
                }
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Code Status"
                readOnly
                className={classes.inputStyle}
                value={clientData?.codeStatus || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Level of Care"
                readOnly
                className={classes.inputStyle}
                value={clientData?.levelOfCare || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Date of last TB Screening"
                readOnly
                className={classes.inputStyle}
                value={
                  moment(clientData?.dateOfLastTBScreening).format(
                    "MM/DD/YYYY",
                  ) || "-"
                }
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Weight (lbs)"
                readOnly
                className={classes.inputStyle}
                value={clientData?.weightLbs || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Blood Pressure"
                readOnly
                className={classes.inputStyle}
                value={clientData?.bloodPressure || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Pulse"
                readOnly
                className={classes.inputStyle}
                value={clientData?.pulse || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Oxygen"
                readOnly
                className={classes.inputStyle}
                value={clientData?.oxygen || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Respiration"
                readOnly
                className={classes.inputStyle}
                value={clientData?.respiration || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Temperature"
                readOnly
                className={classes.inputStyle}
                value={clientData?.temperature || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Glucose Check"
                readOnly
                className={classes.inputStyle}
                value={clientData?.glucoseCheck || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <TextInput
                label="Frequency"
                readOnly
                className={classes.inputStyle}
                value={clientData?.frequency || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <p className={classes.diagnosisBoxLabel}>Diagnosis</p>
              <Box className={classes.diagnosisBox}>
                <Flex className={classes.diagnosisBoxItemsFlex}>
                  {clientData?.diagnosis?.length > 0
                    ? clientData?.diagnosis?.map(({ name }) => (
                        <Flex className={classes.diagnosisBoxItem} key={name}>
                          <p>{name}</p>
                        </Flex>
                      ))
                    : "-"}
                </Flex>
              </Box>
            </Grid.Col>
            <Grid.Col span={12}>
              <p className={classes.diagnosisBoxLabel}>Allergies</p>
              <Box className={classes.diagnosisBox}>
                <Flex className={classes.diagnosisBoxItemsFlex}>
                  {clientData?.allergies?.length > 0
                    ? clientData?.allergies?.map((txt) => (
                        <Flex className={classes.diagnosisBoxItem} key={txt}>
                          <p>{txt}</p>
                        </Flex>
                      ))
                    : "-"}
                </Flex>
              </Box>
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                label="Other/Past History"
                className={classes.inputStyle}
                readOnly
                value={clientData.otherHistory || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <h6 className={classes.sectionHdng}>Emergency Contact</h6>
            </Grid.Col>
            <Grid.Col span={12} md={4}>
              <TextInput
                label="POA Name"
                className={classes.inputStyle}
                readOnly
                value={clientData?.poaName || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Relationship"
                className={classes.inputStyle}
                readOnly
                value={clientData?.relationship || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Phone Number"
                className={classes.inputStyle}
                readOnly
                value={clientData?.poaPhone || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Primary Care Physician"
                className={classes.inputStyle}
                readOnly
                value={clientData?.primaryCarePhysician || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Practice Name"
                className={classes.inputStyle}
                readOnly
                value={clientData?.practiceName || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Phone Number"
                className={classes.inputStyle}
                readOnly
                value={clientData?.pcpPhone || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={6}>
              <TextInput
                label="Pharmacy Name"
                className={classes.inputStyle}
                readOnly
                value={clientData?.pharmacyName || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={6}>
              <TextInput
                label="Phone Number"
                className={classes.inputStyle}
                readOnly
                value={clientData?.pharmacyPhone || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Home Health Agency"
                className={classes.inputStyle}
                readOnly
                value={clientData?.homeHealthAgency || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Services Received"
                className={classes.inputStyle}
                readOnly
                value={clientData?.servicesReceived || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={4}>
              <TextInput
                label="Phone Number"
                className={classes.inputStyle}
                readOnly
                value={clientData?.homeHealthAgencyPhone || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={6}>
              <TextInput
                label="Hospice Agency"
                className={classes.inputStyle}
                readOnly
                value={clientData?.hospiceAgency || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12} md={6}>
              <TextInput
                label="Phone Number"
                className={classes.inputStyle}
                readOnly
                value={clientData?.hospicePhone || "-"}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Textarea
                label="Others"
                className={classes.inputStyle}
                readOnly
                value={clientData?.others || "-"}
              />
            </Grid.Col>
          </Grid>
        </div>
      ),
    },
    {
      label: "Assessment",
      component: (
        <div className={classes.clientDataContainer}>
          <Grid gutter={17}>
            <Grid.Col span={12}>
              <h6 className={classes.sectionHdng}>Physical Assessment</h6>
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Nutrition"
                items={nutritionItems}
                selectedItems={assessment?.nutrition || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Meals Location"
                items={mealLocationItems}
                selectedItems={assessment?.mealsLocation || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Feeding Assistance"
                items={feedingAssistanceItems}
                selectedItems={assessment?.feedingAssistance || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Hydration"
                items={hydrationItems}
                selectedItems={assessment?.hydration || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Assistive Devices"
                items={assistiveDevicesItems}
                selectedItems={assessment?.assistiveDevices || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Vision"
                items={visionItems}
                selectedItems={assessment?.vision || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Speech"
                items={speechItems}
                selectedItems={assessment?.speech || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Hearing"
                items={hearingItems}
                selectedItems={assessment?.hearing || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <ServicePlanAccordion
                title="Toileting"
                items={toileting}
                selectedItems={assessment?.toileting || []}
                view={true}
                fullWidthAccordion={true}
                // className={classes.fullWidthAccordion}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                label="Notes"
                readOnly
                className={classes.inputStyle}
                value={assessment?.physicalAssessmentNotes || "-"}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <h6 className={classes.sectionHdng}>Occupational Assessment</h6>
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Brushing Teeth"
                items={brushingTeeth}
                selectedItems={assessment?.brushingTeeth || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Bed Check"
                items={bedCheck}
                selectedItems={assessment?.bedCheck || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Skin Care"
                items={skinCare}
                selectedItems={assessment?.skinCare || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Nutrition and Hydration"
                items={nutritionAndHydration}
                selectedItems={assessment?.nutritionAndHydration || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <ServicePlanAccordion
                title="Undergarments"
                items={undergarments}
                selectedItems={assessment?.undergarments || []}
                view={true}
                fullWidthAccordion={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Dressing"
                items={dressing}
                selectedItems={assessment?.dressing || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Grooming"
                items={grooming}
                selectedItems={assessment?.grooming || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Shower"
                items={shower}
                selectedItems={assessment?.shower || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Other"
                items={other}
                selectedItems={assessment?.other || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                label="Notes"
                className={classes.inputStyle}
                readOnly
                value={assessment?.occupationalAssessmentNotes || ""}
              />
            </Grid.Col>
          </Grid>
        </div>
      ),
    },

    {
      label: "Activities of Daily Living",
      component: (
        <div className={classes.clientDataContainer}>
          <Grid gutter={17}>
            <Grid.Col span={12}>
              <h6 className={classes.sectionHdng}>
                Activities of Daily Living
              </h6>
            </Grid.Col>
            {taskTypeDetails?.subTasks
              ?.filter(
                (tasks) =>
                  tasks?.task?.name !== "Meals" &&
                  tasks?.task?.name !== "Body Care",
              )
              ?.map((tasks) => {
                const subTaskName = tasks?.task?.name;
                const hiddenFields =
                  hiddenFieldsCareplanAdlConfig[subTaskName] || [];
                const filteredFields =
                  tasks?.fields?.filter(
                    (field) => !hiddenFields.includes(field?.name),
                  ) || [];

                return (
                  <Grid.Col span={12} sm={6} md={3} lg={3} key={tasks?._id}>
                    <AccordionCheck
                      type="view"
                      data-testid="task-grid"
                      tasks={tasks}
                      Data={filteredFields}
                      Heading={tasks?.task?.name}
                      setChecks={setChecks}
                      checks={checks}
                      userType={profile?.user?.type}
                      userId={profile?.user?.id}
                      // shift={profile?.user?.id}
                      shift={""}
                      profile={profile}
                      selectedStaff={profile?.user?.id}
                      selectedStaffData={profile?.user}
                      // requiredCheckedFields={requiredCheckedFields}
                      isFromServicePlan={true}
                    />
                  </Grid.Col>
                );
              })}
          </Grid>
        </div>
      ),
    },

    {
      label: "Care and Safety",
      component: (
        <Box className={classes.clientDataContainer}>
          <Grid gutter={17}>
            <Grid.Col span={12}>
              <h6 className={classes.sectionHdng}>Care and Safety</h6>
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Safety Monitoring"
                items={safetyMonitoringItems}
                selectedItems={careAndSafety?.safetyMonitoring || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Cognitive Activities"
                items={cognitiveActivitiesItems}
                selectedItems={careAndSafety?.cognitiveActivities || []}
                view={true}
              />
            </Grid.Col>

            <Grid.Col span={12} sm={6} md={3} lg={3}>
              {/* <Flex direction="column" gap={18}> */}
              <ServicePlanAccordion
                title="Coordination of Care"
                items={coordinationOfCareItems}
                selectedItems={careAndSafety?.coordinationOfCare || []}
                view={true}
              />
            </Grid.Col>
            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Family/Rep Communication"
                items={familyRepCommunicationItems}
                selectedItems={careAndSafety?.familyRepCommunication || []}
                view={true}
              />
            </Grid.Col>
            {/* </Flex> */}

            <Grid.Col span={12} sm={6} md={6} lg={6}>
              <ServicePlanAccordion
                title="Medication Administration and Treatment"
                items={medicationAdministrationItems}
                selectedItems={careAndSafety?.medicationAdministration || []}
                view={true}
              />
            </Grid.Col>

            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Cognitive Status"
                items={cognitiveStatusItems}
                selectedItems={careAndSafety?.cognitiveStatus || []}
                view={true}
              />
            </Grid.Col>

            <Grid.Col span={12} sm={6} md={3} lg={3}>
              <ServicePlanAccordion
                title="Mobility Ambulation Transfer"
                items={mobilityAmbulationTransferItems}
                selectedItems={careAndSafety?.mobilityAmbulationTransfer || []}
                view={true}
              />
            </Grid.Col>

            <Grid.Col span={12} sm={6} md={6} lg={6}>
              <ServicePlanAccordion
                title="Nursing/Medical Instructions"
                items={nursingMedicalInstructionsItems}
                selectedItems={careAndSafety?.nursingMedicalInstructions || []}
                view={true}
              />
            </Grid.Col>

            <Grid.Col span={12} sm={6} md={6} lg={6}>
              <ServicePlanAccordion
                title="Conditions requiring caregivers to call Physcian"
                items={conditionsRequiringCaregiversItems}
                selectedItems={
                  careAndSafety?.conditionsRequiringCaregivers || []
                }
                view={true}
              />
            </Grid.Col>
          </Grid>
        </Box>
      ),
    },
    {
      label: "Signature",
      component: isAddSignature ? (
        <Box className={classes.addSignatureContainer}>
          <Flex className={classes.signatureInfoRow}>
            <Select
              label="Signee Designation"
              placeholder="Select Signee Designation"
              className={cx(classes.inputStyle, classes.flex1)}
              value={signatureItem?.position || null}
              disabled={userSign ? true : false}
              onChange={(pos) => {
                const item = {
                  id: uuidv4(),
                  fullName: "",
                  title: "",
                  date: new Date(),
                  signatureRequestStatus: "pending",
                  position: pos,
                  email: decoded?.email,
                };
                setSignatureItem(item);
              }}
              rightSection={<IconChevronDown />}
              data={[
                {
                  label: "Client/POA",
                  value: "Client/POA",
                },
                {
                  label: "Facility",
                  value: "Facility",
                  disabled: true
                },
                {
                  label: "Medical Professional",
                  value: "Medical Professional",
                },
                {
                  label: "Other",
                  value: "Other",
                },
              ]}
            />
            <TextInput
              label="Full Name"
              placeholder="Add Name"
              value={signatureItem?.fullName}
              onChange={(e) => {
                handleSignFieldChange("fullName", e.target.value);
              }}
              className={cx(classes.inputStyle, classes.flex1)}
              error={error.fullName}
            />

            <TextInput
              label="Title"
              placeholder="Add Title"
              value={signatureItem?.title}
              onChange={(e) => {
                handleSignFieldChange("title", e.target.value);
              }}
              className={cx(classes.inputStyle, classes.flex1)}
              error={error.title}
            />

            <DateInput
              className={cx(classes.inputStyle, classes.flex1)}
              icon={<FiCalendar />}
              label="Date"
              valueFormat="MMM D, YYYY"
              w={"25%"}
              placeholder="mm/dd/yyyy"
              value={
                signatureItem?.date
                  ? moment(signatureItem?.date).toDate()
                  : null
              }
              onChange={(value) => {
                handleSignFieldChange("date", value);
              }}
              error={error.date}
            />
          </Flex>
          <Box className={classes.addSignatureWrapper}>
            <Tabs
              className={classes.tabs}
              defaultValue={"draw"}
              value={currentTab}
              onTabChange={handleTabChange}
            >
              <Tabs.List justify="center" className={classes.tab_list}>
                <Tabs.Tab className={classes.tab} value="draw" c={"#979797"}>
                  <FiEdit size={20} /> Draw
                </Tabs.Tab>
                <Tabs.Tab className={classes.tab} value="type" c={"#979797"}>
                  <TbKeyboard size={25} /> Type
                </Tabs.Tab>
                <Tabs.Tab className={classes.tab} value="image" c={"#979797"}>
                  <CiImageOn size={25} /> Image
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="draw" className={classes.tabPanel}>
                <div className={classes.signaturePadParent}>
                  <canvas ref={signatureCanvasRef} />
                  {error.signature != "" ? (
                    <Text fs={14} fw={500} color="red">
                      {error.signature}
                    </Text>
                  ) : (
                    ""
                  )}
                </div>
              </Tabs.Panel>
              <Tabs.Panel value="type" className={classes.tabPanel}>
                <Flex
                  align="center"
                  justify="center"
                  className={classes.textPad}
                >
                  <TextInput
                    variant="unstyled"
                    value={signatureTextValue}
                    onChange={(event) =>
                      setSignatureTextValue(event.currentTarget.value)
                    }
                  />
                </Flex>
                {error.signature != "" ? (
                  <Text fs={14} fw={500} color="red">
                    {error.signature}
                  </Text>
                ) : (
                  ""
                )}
              </Tabs.Panel>
              <Tabs.Panel value="image" className={classes.tabPanel}>
                <Box>
                  {selectedImage ? (
                    <Flex
                      align="center"
                      justify="center"
                      className={classes.previewBox}
                    >
                      <Box pos="relative" display="inline-block">
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="preview"
                          className={classes.previewImg}
                        />

                        <ActionIcon
                          variant="transparent"
                          className={classes.selectedImgClose}
                          onClick={() => setSelectedImage(null)}
                        >
                          <IoIosCloseCircle size={24} color="#FFF" />
                        </ActionIcon>
                      </Box>
                    </Flex>
                  ) : (
                    <Dropzone
                      accept={IMAGE_MIME_TYPE}
                      onDrop={(files) => setSelectedImage(files[0])}
                      maxSize={2 * 1024 ** 2}
                      multiple={false}
                      classNames={{
                        root: classes.uploadBox,
                        inner: "uploadBoxInner",
                      }}
                    >
                      <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        h="100%"
                        w="100%"
                      >
                        <Text fz={16} fw={600} color="#171717" ff="Inter">
                          <Text
                            span
                            td="underline"
                            fz={16}
                            fw={600}
                            color="#171717"
                            ff="Inter"
                          >
                            Click to Upload
                          </Text>{" "}
                          or Drag and Drop
                        </Text>
                        <Text fz={12} fw={500} c="#979797" ff="Inter" mt={8}>
                          Maximum File Size 2MB
                        </Text>
                      </Flex>
                    </Dropzone>
                  )}
                </Box>
                {error.signature != "" ? (
                  <Text fs={14} fw={500} color="red">
                    {error.signature}
                  </Text>
                ) : (
                  ""
                )}
              </Tabs.Panel>
            </Tabs>
            <Flex justify="flex-end" my={10} gap={10}>
              <Button
                className={classes.cancelBtn}
                onClick={handleClearSignature}
              >
                Clear
              </Button>
              <Button
                className={classes.submitBtn}
                disabled={loading || !signatureItem}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Flex>
          </Box>
        </Box>
      ) : (
        <>
          <Grid gutter={32} className={classes.signaturesGrid}>
            {userSign ? (
              <Grid.Col span={12}>
                <Box className={classes.signatureParentBox}>
                  <p className={classes.signatureParentBoxPosition}>
                    {userSign?.position}
                  </p>
                  <Flex className={classes.signBoxInputsRow}>
                    <TextInput
                      variant="unstyled"
                      label="Name"
                      className={classes.signBoxInputsStyle}
                      withAsterisk
                      value={userSign?.fullName}
                      readOnly
                    />
                    <TextInput
                      variant="unstyled"
                      label="Title"
                      className={classes.signBoxInputsStyle}
                      withAsterisk
                      readOnly
                      value={userSign?.title}
                    />
                    <Box className={classes.flex1}>
                      {mode !== "view" && (
                        <ActionIcon
                          variant="subtle"
                          ml="auto"
                          onClick={() => {
                            setIsAddSignature(true);
                            setSignatureItem(userSign);
                            setCurrentTab("draw");
                            handleClearSignature();
                          }}
                        >
                          <FaUndo />
                        </ActionIcon>
                      )}
                      <Box className={classes.signViewBox}>
                        {userSign?.signature || userSign?.signatureImage ? (
                          <img
                            src={
                              userSign?.signature || userSign?.signatureImage
                            }
                          />
                        ) : userSign?.signatureText ? (
                          <p className="textSign">{userSign?.signatureText}</p>
                        ) : null}
                      </Box>
                      <p className={classes.signDrawBoxLabel}>
                        Signature<span>*</span>
                      </p>
                    </Box>
                    <DateInput
                      variant="unstyled"
                      label="Date"
                      className={`${classes.signBoxInputsStyle} ${classes.borderBottomGray}`}
                      withAsterisk
                      value={userSign?.date ? new Date(userSign?.date) : null}
                      readOnly
                    />
                  </Flex>
                </Box>
              </Grid.Col>
            ) : (
              <Grid.Col>
                <Text ta="center" className={classes.planTypeHdng}>
                  No signature Found
                </Text>
              </Grid.Col>
            )}
          </Grid>
        </>
      ),
    },
  ];

  return (
    <Box className={classes.containerr}>
      <Box className={classes.header}>
        <img src="/assets/images/logo2.png" alt="Logo" />
        <h2 className={classes.reviewDocHdng}>Review Document</h2>
        <Box w="10%"></Box>
      </Box>
      <Tabs
        value={active.toString()}
        onTabChange={(value) => {
          if (isAddSignature) {
            setError(initialErrorState);
            setIsAddSignature(false);
            setSignatureItem(null);
          }
          setActive(Number(value));
        }}
        classNames={{
          tab: classes.mainTab,
          tabsList: classes.mainTabsList,
        }}
      >
        <Tabs.List grow>
          {steps.map((step, index) => (
            <Tabs.Tab value={index.toString()} key={index}>
              {step.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {steps.map((step, index) => (
          <Tabs.Panel value={index.toString()} key={index}>
            <Box className={classes.stepContent}>
              <Box className={classes.stepHeader}>
                <h2 className={classes.planTypeHdng}>
                  {decoded?.businessName || "Service Plan"}
                </h2>
                <p className={classes.sentDate}>
                  Sent: {moment(decoded?.sentDate).format("MM/DD/YYYY")}
                </p>
              </Box>
              {step.component}
            </Box>
          </Tabs.Panel>
        ))}
      </Tabs>

      <Box className={classes.tabFooter}>
        <Button
          variant="subtle"
          className={classes.navBtn}
          leftIcon={<IconChevronLeft />}
          onClick={() => {
            if (isAddSignature) {
              setError(initialErrorState);
              setIsAddSignature(false);
              setSignatureItem(null);
            } else {
              setActive((prev) => Math.max(0, prev - 1));
            }
          }}
          disabled={active === 0}
        >
          Back
        </Button>
        <Button
          variant="subtle"
          className={classes.navBtn}
          rightIcon={<IconChevronRight />}
          onClick={() =>
            setActive((prev) => Math.min(steps.length - 1, prev + 1))
          }
          disabled={active === steps.length - 1}
        >
          Next
        </Button>
      </Box>

      {!signatureItem && !isAddSignature && (
        <Flex gap={20} justify="center" m={20}>
          {mode !== "view" && !userSign && (
            <Button
              onClick={() => {
                setActive(4);
                setIsAddSignature(true);
              }}
              className={cx(classes.submitBtn, classes.greenBtnStyle)}
            >
              <WhiteCheckMarkSvg />
              Approve & Sign
            </Button>
          )}
          <Button className={cx(classes.submitBtn)} onClick={handleDownloadPDF}>
            Download
          </Button>
        </Flex>
      )}
    </Box>
  );
};
